import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Icon,
  InlineMessage,
  InputField,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import testIds from '../../../../constants/testIds';
import { type RootState as HostRoot } from '../../../../services/serviceTypes';
import extractValidationPattern, {
  type ParsedValidationResult,
} from '../../../../utils/extractValidationPattern';

import './TextInput.css';

type InputFieldKind = 'singleline' | 'multiline' | 'search';

interface FormInputTextProps {
  label: string | null;
  defaultValue: string | undefined;
  password: boolean;
  required: boolean;
  pattern: string | null;
  guideMe: boolean;
  fieldDescription: string | null;
  payloadAtrributeName: string;
  attributeName: string;
  fieldDisabled: boolean;
  kind?: InputFieldKind;
  isTooltip?: boolean;
  tooltipIconColor?: string;
  tooltipText?: string;
  placeholder?: string | undefined;
  errorMessage?: string;
  hasError?: boolean;
  inputValueStatus: (status: {
    attributeName: string;
    value: string;
    hasError: boolean;
    payloadAtrributeName: string;
  }) => void;
}

/**
 * Form Input Text Field
 */
function FormInputText({
  label,
  defaultValue,
  password,
  required,
  pattern,
  guideMe,
  fieldDescription,
  payloadAtrributeName,
  attributeName,
  fieldDisabled,
  kind,
  isTooltip,
  tooltipIconColor,
  tooltipText,
  inputValueStatus,
  errorMessage,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  hasError: externalHasError,
  placeholder,
}: FormInputTextProps) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState(defaultValue || '');
  const [visible, setVisible] = useState(!password);
  const [hasError, setHasError] = useState(false);
  const { messages } = useSelector((state: HostRoot) => state.user);

  const patternObject: ParsedValidationResult = pattern
    ? extractValidationPattern(pattern)
    : null;

  const validateInput = React.useCallback(
    (value: string) => {
      let isValid = true;
      let hasValidationError = false;

      // Check required field
      if (required && !value.trim()) {
        isValid = false;
        hasValidationError = true;
      }

      // Check pattern validation
      if (
        (patternObject?.type === 'patterns' ||
          patternObject?.type === 'unknown') &&
        value.length > 0
      ) {
        let patternData;

        if (patternObject.type === 'patterns') {
          const [firstPattern] = patternObject.patterns;

          patternData = firstPattern;
        } else if (patternObject.type === 'unknown') {
          const originalString = String(patternObject.original);
          const patternMatch = originalString.match(/"pattern":"([^"]+)"/);
          const errorMatch = originalString.match(/"error":"([^"]+)"/);

          if (patternMatch && errorMatch) {
            const [, patternValue] = patternMatch;
            const [, errorValue] = errorMatch;

            patternData = {
              pattern: patternValue
                .replace(/\\\\/g, '\\')
                .replace(/\\\//g, '/'),
              error: errorValue,
            };
          }
        }

        if (patternData?.pattern) {
          try {
            const regex = new RegExp(patternData.pattern);

            if (!regex.test(value)) {
              isValid = false;
              hasValidationError = true;
            }
          } catch (e) {
            // Invalid regex, skip validation
          }
        }
      }

      setHasError(hasValidationError);

      return { isValid, hasValidationError };
    },
    [patternObject, required]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setInputValue(value);
    validateInput(value);
  };

  const handleMaxLength = (value: ParsedValidationResult): number => {
    if (value?.type === 'maxLength') {
      return Number(value.maxLength);
    }

    return 999;
  };

  const handleValidationMessageString = (): string => {
    let validationString = '';

    // Required field validation
    if (required && !inputValue.trim()) {
      validationString = intl.formatMessage({
        id: 'FIELD_REQUIRED',
        defaultMessage: 'This field is required',
      });

      return validationString;
    }

    if (patternObject?.type === 'maxLength') {
      if (inputValue.length === Number(patternObject.maxLength)) {
        validationString = intl.formatMessage({
          id: 'INTEGRATIONS.SERVICENOW.INPUT.MAXLENGTH',
          defaultMessage: `Maximum ${patternObject.maxLength} is Allowed`,
        });
      }
    }

    if (
      patternObject?.type === 'patterns' ||
      patternObject?.type === 'unknown'
    ) {
      let patternData;

      if (patternObject.type === 'patterns') {
        const [firstPattern] = patternObject.patterns;

        patternData = firstPattern;
      } else if (patternObject.type === 'unknown') {
        const originalString = String(patternObject.original);
        const patternMatch = originalString.match(/"pattern":"([^"]+)"/);
        const errorMatch = originalString.match(/"error":"([^"]+)"/);

        if (patternMatch && errorMatch) {
          const [, patternValue] = patternMatch;
          const [, errorValue] = errorMatch;

          patternData = {
            pattern: patternValue.replace(/\\\\/g, '\\').replace(/\\\//g, '/'),
            error: errorValue,
          };
        }
      }

      if (patternData?.pattern && inputValue.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const regex = new RegExp(patternData.pattern);

        if (!regex.test(inputValue)) {
          const errorKey = patternData.error;
          const messageFromStore = messages[errorKey] as string | undefined;

          if (messageFromStore) {
            validationString = messageFromStore;
          } else {
            validationString = intl.formatMessage({
              id: errorKey,
              defaultMessage:
                errorKey === 'AOB.CUA.URL.VALIDATION_ERROR1'
                  ? 'Please enter a valid URL'
                  : 'Invalid input format',
            });
          }
        }
      }
    }

    return validationString;
  };

  const handleValidationMessage = (
    patternValue: ParsedValidationResult
  ): React.ReactNode => {
    if (patternValue === null) {
      return null;
    }

    if (
      patternValue.type === 'maxLength' &&
      inputValue.length === Number(patternValue.maxLength)
    ) {
      return (
        <InlineMessage
          text={handleValidationMessageString()}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      );
    }

    if (
      (patternValue.type === 'patterns' || patternValue.type === 'unknown') &&
      hasError
    ) {
      const errorText = handleValidationMessageString();

      return (
        <InlineMessage
          text={errorText}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      );
    }

    // Show required field error
    if (required && !inputValue.trim() && hasError) {
      return (
        <InlineMessage
          text={handleValidationMessageString()}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      );
    }

    return null;
  };

  useEffect(() => {
    if (inputValueStatus) {
      inputValueStatus({
        attributeName,
        value: inputValue,
        hasError,
        payloadAtrributeName,
      });
    }
  }, [
    hasError,
    inputValue,
    inputValueStatus,
    attributeName,
    payloadAtrributeName,
  ]);

  const handleClear = (value: string) => {
    if (value === '') {
      setInputValue('');
      setHasError(false);
    }
  };

  return (
    <>
      <InputField
        className='aob-textInput'
        dataTestId={`${testIds.DISCOVERY_FORM_BUILDER_INPUT_FIELD}--${attributeName}`}
        name='basic'
        placeholder={placeholder ?? ''}
        kind={kind}
        label={
          <Typography
            className='aob-textInput-label'
            color={
              fieldDisabled
                ? '--color-foreground-neutral-subtle'
                : '--color-foreground-neutral-bold'
            }
            kind='label'
            htmlFor='Critical'>
            {label}
            {isTooltip && (
              <Tooltip
                className='aob-textInput-label-tooltip'
                placement='top-start'
                maxWidth='700'
                trigger={
                  <Icon
                    className='aob-textInput-label-icon'
                    kind='AlertInfoFilledSeparateColors'
                    color={tooltipIconColor || '--color-foreground-info-subtle'}
                    size='small'
                  />
                }
                text={
                  tooltipText
                    ? intl.formatMessage({
                        id: tooltipText,
                        defaultMessage: 'Tooltip text',
                      })
                    : intl.formatMessage({
                        id: 'Tooltip text',
                        defaultMessage: 'Tooltip text',
                      })
                }
              />
            )}
          </Typography>
        }
        shouldDisallowBrackets={false}
        value={inputValue}
        setValue={handleClear}
        onChange={handleInputChange}
        maxLength={handleMaxLength(patternObject)}
        isRequired={required}
        isCritical={
          hasError ||
          (patternObject?.type === 'maxLength' &&
            inputValue.length === Number(patternObject.maxLength))
        }
        isDisabled={fieldDisabled}
        setIsCritical={() => {}}
        suffixButtonType={password ? 'password' : undefined}
        suffixIsToggle={!visible}
        type={visible ? 'text' : 'password'}
        suffixOnClick={() => setVisible(!visible)}
        CriticalHelperText={handleValidationMessage(patternObject)}
        HelperText={
          guideMe && (
            <div data-testid='helper-text'>
              {fieldDescription?.split('\n').map((line) => (
                <Typography
                  kind='body3'
                  color='--color-foreground-neutral-medium'>
                  {line}
                </Typography>
              ))}
            </div>
          )
        }
      />
      {errorMessage && (
        <div style={{ marginTop: '8px' }}>
          <InlineMessage
            text={errorMessage}
            colorTheme='critical'
            size='small'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        </div>
      )}
    </>
  );
}

FormInputText.defaultProps = {
  isTooltip: false,
  tooltipIconColor: '',
  tooltipText: '',
  placeholder: null,
  kind: 'singleline',
  errorMessage: null,
  hasError: false,
};

export default FormInputText;
